/**
 * messageTimeline — turn a message's ordered `parts` array into an ordered list
 * of render segments. Public entry point.
 *
 * WHY THIS EXISTS: `parts` is already chronological — narration text, reasoning,
 * and tool calls appear in the order they streamed. Rendering it by TYPE ("all
 * text, then all tools") puts narration that PRECEDED a tool call above it and
 * makes the tool work read as a footnote appended after the final answer. That
 * inverts the timeline: a turn that actually went
 *
 *   "let me look up ACME" → find_company → "checking their deals" → list_deals → "here's the summary"
 *
 * rendered as one merged text blob followed by a single "Used 2 tools" box.
 * This module preserves the real order instead.
 *
 * GROUPING RULE: CONSECUTIVE tool invocations coalesce into one collapsible run.
 * Any text / reasoning / artifact / context part closes the run. `step-start`
 * markers do NOT close it — two calls in different model steps with nothing said
 * between them are still one continuous stretch of work.
 *
 * Deliberately excluded from the timeline:
 * - `file` parts. The AI SDK puts a user's attachment parts BEFORE their text
 *   part, so timelining them would render attachment cards above the message
 *   they belong to. They come back as `fileParts` for the caller to render in
 *   its own slot.
 * - `update_plan` calls. The model re-sends the full step list on every call, so
 *   the checklist renders ONCE (latest payload wins) anchored where the first
 *   call appeared — it updates in place instead of stacking or fragmenting the
 *   surrounding tool run.
 *
 * LAYOUT: the pieces live in `./message-timeline/` — `types` (the render
 * vocabulary, logic-free so a component can import a segment kind without the
 * builder), `text` (invisible-character hygiene), `tool-parts` (reading an
 * untrusted part, incl. the streaming-vs-persisted tool-name rule), `build`
 * (the single ordered walk), `turn-layout` (visual weight: what folds into the
 * activity strip). `ChatMessageItem`, `ToolBlock`, `TurnActivityBlock` and
 * apps/hub's tests keep importing from THIS path.
 */

export { stripInvisibleChars } from './message-timeline/text.js';

export { resolveToolName } from './message-timeline/tool-parts.js';

export { buildMessageTimeline } from './message-timeline/build.js';

export { buildTurnLayout } from './message-timeline/turn-layout.js';

export type {
  ActivityBlock,
  ApprovalSegment,
  ArtifactSegment,
  ContextSegment,
  MessageFilePart,
  MessageTimeline,
  PlanSegment,
  ReasoningSegment,
  StandaloneBlock,
  TextSegment,
  TimelineSegment,
  ToolsSegment,
  TurnLayoutBlock,
} from './message-timeline/types.js';
